import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { getTimesheets } from '../../../services/timesheetService';
import formatMinutes from '../../../utils/formatMinutes';
import timesheetStatusColors from '../../../utils/timesheetStatusColors';
import { translateTimesheetStatuses } from '../../../utils/timesheetStatusLabels';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import PersonCell from '../../../components/ui/PersonCell';
import AnomalyBadges from '../../../components/ui/AnomalyBadges';

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $bg }) => $bg || '#9e9e9e15'};
  color: ${({ $color }) => $color || '#9e9e9e'};
`;

// AKM POC §7.6 — liste sütunları: erken çıkış, eksik süre, hesaplanan ve onaylanan fazla mesai, planlanan vardiya.
const useTimesheetApi = (queryParams) => {
  const { t, i18n } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    getTimesheets(paramsToUse)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .catch(() => {
        setData([]);
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => {
    setRefreshTrigger((p) => p + 1);
  }, []);

  // Tek durum haritası (utils/timesheetStatusLabels) — partialLeave dahil hiçbir durum ham görünmez.
  const statusLabels = useMemo(() => translateTimesheetStatuses(t), [t]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'userFullName', label: t('timesheet.employee'),
      options: {
        filter: true, sort: true,
        filterType: 'textField',
        filterOptions: { defaultOp: 'like' },
        customBodyRender: (v, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <PersonCell name={v} profilePicture={row?.userProfilePicture} />;
        },
      },
    },
    dateFilterColumn('date', t('timesheet.date'), i18n.language),
    {
      name: 'workHourName', label: t('timesheet.workHour'),
      options: { filter: false, sort: false },
    },
    {
      // AKM POC §7.6 — planlanan vardiya saatleri (günün çözülmüş vardiyası; plan tabanlı günlerde de dolu).
      name: 'plannedShift', label: t('taReports.columns.plannedShift'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row?.plannedStart || !row?.plannedEnd) return '-';
          return `${String(row.plannedStart).substring(0, 5)}–${String(row.plannedEnd).substring(0, 5)}`;
        },
      },
    },
    {
      name: 'expectedMinutes', label: t('timesheet.expected'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => formatMinutes(v),
      },
    },
    {
      name: 'actualMinutes', label: t('timesheet.actual'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => formatMinutes(v),
      },
    },
    {
      name: 'lateMinutes', label: t('timesheet.late'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => formatMinutes(v),
      },
    },
    {
      name: 'earlyLeaveMinutes', label: t('taReports.columns.earlyLeave'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
    },
    {
      name: 'undertimeMinutes', label: t('taReports.undertime.column'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
    },
    {
      name: 'outsideMinutes', label: t('timesheet.outside'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => formatMinutes(v),
      },
    },
    {
      name: 'totalComputedOvertimeMinutes', label: t('taReports.columns.computedOvertime'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
    },
    {
      name: 'approvedOvertimeMinutes', label: t('taReports.columns.approvedOvertime'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
    },
    {
      name: 'status', label: t('timesheet.status'),
      options: {
        filter: true, sort: true,
        filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: Object.keys(timesheetStatusColors),
          renderValue: (v) => statusLabels[v] || v,
        },
        filterList: [],
        customFilterListOptions: { render: (v) => statusLabels[v] || v },
        customBodyRender: (v) => {
          const colors = timesheetStatusColors[v] || { bg: '#9e9e9e15', text: '#9e9e9e' };
          return <StatusBadge $bg={colors.bg} $color={colors.text}>{statusLabels[v] || v}</StatusBadge>;
        },
      },
    },
    {
      name: 'anomalies', label: t('timesheet.anomalies'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => <AnomalyBadges codes={v} />,
      },
    },
  ], [t, i18n.language, data, statusLabels]);

  return { columns, data, fetching, pagination, refresh, statusLabels };
};

export default useTimesheetApi;
